import type * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';
import { Slot as SlotPrimitive } from 'radix-ui';

import { cn } from './lib/cn';

/**
 * Typography scale based on the design system:
 *
 * Display (font-inter):
 * - display-lg: 36px/44px, -1.5% tracking
 * - display-md: 28px/36px, -1px tracking
 * - display-sm: 22px/28px, -1px tracking
 *
 * Heading (font-inter):
 * - heading-xl: 40-52px/48-60px (responsive)
 * - heading-lg: 34-40px/38-48px (responsive)
 * - heading-md: 24-28px/28-34px (responsive)
 * - heading-sm: 18-24px/22-28px (responsive)
 *
 * Title (font-inter):
 * - title-lg: 20px/28px, medium
 * - title-md: 16px/24px, medium
 * - title-sm: 14px/20px, medium
 *
 * Body (font-inter):
 * - body-lg: 16px/24px
 * - body-md: 14px/20px
 * - body-sm: 12px/18px
 *
 * Label (font-inter):
 * - label-lg: 14px/20px, medium
 * - label-md: 12px/16px, medium
 * - label-sm: 11px/16px, medium
 *
 * Caption (font-inter):
 * - caption: 10px/14px
 */

const textVariants = cva('', {
  variants: {
    /**
     * Display variants - Large promotional/hero text
     */
    display: {
      lg: 'font-inter font-semibold text-[36px] leading-[44px] tracking-[-0.015em]',
      md: 'font-inter font-semibold text-[28px] leading-[36px] tracking-[-1px]',
      sm: 'font-inter font-medium text-[22px] leading-[28px] tracking-[-1px]',
    },

    /**
     * Heading variants - Section headings with serif font
     */
    heading: {
      xl: 'text-[40px] leading-[48px]',
      lg: 'text-[32px] leading-[40px]',
      md: 'text-[24px] leading-[28px]',
      sm: 'text-[18px] leading-[22px]',
    },

    /**
     * Title variants - Component titles and card headers
     */
    title: {
      lg: 'font-inter font-medium text-[22px] text-role-on-background leading-[28px]',
      md: 'font-inter font-medium text-[18px] text-role-on-background leading-[24px]',
      sm: 'font-inter font-medium text-[16px] text-role-on-background leading-[22px]',
      xs: 'font-inter font-medium text-[14px] text-role-on-background leading-[18px]',
    },

    /**
     * Body variants - Paragraph and general content text
     */
    body: {
      lg: 'font-inter text-[16px] leading-[24px]',
      md: 'font-inter text-[14px] leading-[20px]',
      sm: 'font-inter text-[12px] leading-[18px]',
      xs: 'font-inter text-[11px] leading-[16px]',
    },

    /**
     * Label variants - Form labels, buttons, and UI elements
     */
    label: {
      lg: 'font-inter font-medium text-[14px] leading-[20px]',
      md: 'font-inter font-medium text-[12px] leading-[16px]',
      sm: 'font-inter font-medium text-[11px] leading-[16px]',
    },

    /**
     * Caption variant - Small helper text, timestamps
     */
    caption: {
      default: 'font-inter text-[10px] leading-[14px]',
    },

    /**
     * Color variants using role-based colors
     */
    color: {
      default: 'text-role-on-background',
      subtle: 'text-role-on-background-subtle',
      invert: 'text-role-on-background-invert',
      primary: 'text-role-primary',
      secondary: 'text-role-secondary',
      'on-background': 'text-role-on-background',
      'secondary-on-container': 'text-role-secondary-on-container',
      'on-background-subtle': 'text-role-on-background-subtle',
      info: 'text-role-info',
      'on-info': 'text-role-on-info',
      success: 'text-role-success',
      error: 'text-role-error',
      alert: 'text-role-alert',
      disabled: 'text-role-on-disabled',
      surface: 'text-role-on-surface',
      'on-surface-neutral': 'text-role-on-surface-neutral',
      'surface-subtle': 'text-role-on-surface-neutral',
      inherit: 'text-inherit',
    },

    /**
     * Weight variants
     */
    weight: {
      light: 'font-light',
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },

    /**
     * Alignment variants
     */
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },

    /**
     * Truncation variants
     */
    truncate: {
      true: 'truncate',
      1: 'line-clamp-1',
      2: 'line-clamp-2',
      3: 'line-clamp-3',
    },
  },
  defaultVariants: {
    color: 'default',
  },
});

type TextVariants = VariantProps<typeof textVariants>;

type TextElement = 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'label' | 'div';

interface TextProps extends Omit<React.HTMLAttributes<HTMLElement>, 'color' | 'title'>, TextVariants {
  /**
   * The HTML element to render. Will be automatically inferred from variant if not provided.
   */
  as?: TextElement;
  /**
   * Change the default rendered element for the one passed as a child,
   * merging their props and behavior.
   */
  asChild?: boolean;
}

const HEADING_TAG_MAP: Record<NonNullable<TextVariants['heading']>, TextElement> = {
  xl: 'h1',
  lg: 'h2',
  md: 'h3',
  sm: 'h4',
};

const DISPLAY_TAG_MAP: Record<NonNullable<TextVariants['display']>, TextElement> = {
  lg: 'h1',
  md: 'h2',
  sm: 'h3',
};

function getDefaultElement(variants: TextVariants): TextElement {
  if (variants.heading) {
    return HEADING_TAG_MAP[variants.heading];
  }
  if (variants.display) {
    return DISPLAY_TAG_MAP[variants.display];
  }
  if (variants.title) {
    return 'h4';
  }
  if (variants.label) {
    return 'label';
  }
  return 'p';
}

function Text({
  className,
  asChild = false,
  as,
  display,
  heading,
  title,
  body,
  label,
  caption,
  color,
  weight,
  align,
  truncate,
  children,
  ...props
}: TextProps) {
  const variants = { display, heading, title, body, label, caption, color, weight, align, truncate };
  const Component = asChild ? SlotPrimitive.Slot : (as ?? getDefaultElement(variants));

  return (
    <Component className={cn(textVariants(variants), className)} {...props}>
      {children}
    </Component>
  );
}

Text.displayName = 'Text';

export type { TextProps, TextVariants };
export { Text, textVariants };
